// components/Common/ContentWatermark.js
// Lightweight watermark overlay for content protection on videos & documents.
// Shows student name + institute/brand name in a repeating diagonal pattern.
// `videoMode` adds a custom fullscreen toggle so the wrapper (not the video)
// goes fullscreen, keeping the watermark visible.
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Maximize, Minimize } from 'lucide-react';
import { getCurrentUser } from '../../utils/auth';
import { useBranding } from '../../contexts/BrandingContext';
import './ContentWatermark.scss';

const ContentWatermark = ({ videoMode = false }) => {
  const user = getCurrentUser();
  const { branding } = useBranding();
  const [isFs, setIsFs] = useState(false);
  const wrapperRef = useRef(null);
  const [drift, setDrift] = useState({ x: 0, y: 0 });

  const text = useMemo(() => {
    const parts = [];
    if (user?.name) parts.push(user.name);
    if (branding?.brandName) parts.push(branding.brandName);
    return parts.join('  •  ') || '';
  }, [user?.name, branding?.brandName]);

  // Session ID for tracing leaked recordings
  const sessionId = useMemo(() => Math.random().toString(36).slice(2, 8).toUpperCase(), []);

  // Timestamp line — updates every 30s
  const [timestamp, setTimestamp] = useState(() => new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
  useEffect(() => {
    if (!videoMode) return;
    const id = setInterval(() => setTimestamp(new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })), 30000);
    return () => clearInterval(id);
  }, [videoMode]);

  // Random drift every 20s — shifts watermark slightly so it can't be masked with a static overlay
  useEffect(() => {
    if (!videoMode) return;
    const id = setInterval(() => {
      setDrift({ x: Math.round(Math.random() * 30 - 15), y: Math.round(Math.random() * 20 - 10) });
    }, 20000);
    return () => clearInterval(id);
  }, [videoMode]);

  // Track fullscreen state
  useEffect(() => {
    if (!videoMode) return;
    const handler = () => {
      const el = wrapperRef.current?.parentElement;
      setIsFs(!!document.fullscreenElement && document.fullscreenElement === el);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [videoMode]);

  const toggleFs = useCallback(() => {
    const el = wrapperRef.current?.parentElement;
    if (!el) return;
    if (document.fullscreenElement === el) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  if (!text) return null;

  return (
    <div className="content-watermark" ref={wrapperRef} aria-hidden="true"
      style={videoMode ? { transform: `translate(${drift.x}px, ${drift.y}px)`, transition: 'transform 2s ease' } : undefined}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="content-watermark__row">
          <span className="content-watermark__text">{text}</span>
          <span className="content-watermark__text">{text}</span>
          <span className="content-watermark__text">{text}</span>
        </div>
      ))}
      {videoMode && (
        <div className="content-watermark__trace">
          {timestamp} • {sessionId}
        </div>
      )}
      {videoMode && (
        <button
          className="content-watermark__fs-btn"
          onClick={toggleFs}
          title={isFs ? 'Exit fullscreen' : 'Fullscreen'}
          aria-hidden="false"
        >
          {isFs ? <Minimize size={18} /> : <Maximize size={18} />}
        </button>
      )}
    </div>
  );
};

export default ContentWatermark;
